import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import i18n from 'i18n-js';
import {
    Button,
    ListItemText,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import VisibilityIcon from '@material-ui/icons/Visibility';

import { BasicThreeDotsMenu, NothingFoundMessage, PopupConfirmation } from 'components/shared';
import { useAppSelector } from 'index';
import tableMapsStore from 'store/tableMapsStore';
import { theme } from 'config/theme';
import { TableMapListItem } from 'store/tableMapsStore/reducer';
import { Positions, Roles } from 'constants/enums';
import { useStyles as useRestaurantStyles } from 'components/restaurants/restaurantsPage/styles';
import { makeStyles } from '@material-ui/core';

const useLocalStyles = makeStyles({
    statusDot: {
        display: 'inline-block',
        width: 8,
        height: 8,
        borderRadius: '50%',
        marginRight: 6,
        flexShrink: 0,
    },
    statusCell: {
        display: 'flex',
        alignItems: 'center',
    },
    updatedDate: {
        fontSize: 13,
        color: theme.palette.text.primary,
        lineHeight: 1.3,
    },
    updatedTime: {
        fontSize: 12,
        color: theme.palette.text.secondary,
        lineHeight: 1.3,
    },
    nameLink: {
        cursor: 'pointer',
        color: theme.palette.primary.main,
        fontWeight: 600,
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    titleGroup: {
        display: 'flex',
        flexDirection: 'column',
    },
    title: {
        fontSize: 18,
        fontWeight: 600,
        color: theme.palette.primary.dark,
        lineHeight: 1.2,
    },
    subtitle: {
        fontSize: 12,
        color: theme.palette.text.secondary,
        marginTop: 2,
    },
    createButton: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        fontWeight: 600,
        letterSpacing: 1.25,
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
    },
    paper: {
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
});

function formatUpdatedAt(iso: string | null): { date: string; time: string } | null {
    if (!iso) return null;
    const d = new Date(iso);
    const date = d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
    const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    return { date, time };
}

export default function TableMapsTable() {
    const classes = useLocalStyles();
    const sharedClasses = useRestaurantStyles();
    const dispatch = useDispatch();
    const history = useHistory();

    const list = useAppSelector((state) => state.tableMaps.list);
    const user = useAppSelector((state) => state.user);
    const currentRestaurant = user.restaurants?.find((r) => r.id === user.currentRestaurantId);

    const isManagerOrOwner =
        user.role === Roles.OWNER || currentRestaurant?.currentPosition === Positions.MANAGER;

    const [deleteTarget, setDeleteTarget] = useState<TableMapListItem | null>(null);

    useEffect(() => {
        dispatch(tableMapsStore.actionCreators.getTableMaps());
    }, []);

    function handleCreate() {
        history.push('/table-maps/create');
    }

    function handleView(id: string) {
        history.push(`/table-maps/${id}`);
    }

    function handleEdit(id: string) {
        history.push(`/table-maps/${id}/edit`);
    }

    function handleDeleteConfirm() {
        if (deleteTarget) {
            dispatch(tableMapsStore.actionCreators.deleteTableMap(deleteTarget.id));
        }
        setDeleteTarget(null);
    }

    function buildMenuItems(row: TableMapListItem): JSX.Element[] {
        return [
            <div
                className={sharedClasses.threeDotsMenuItemContainer}
                onClick={() => handleView(row.id)}
                key="view"
            >
                <VisibilityIcon className={sharedClasses.threeDotsItemIcon} />
                <ListItemText primary={i18n.t('tableMaps.actionViewDetails')} />
            </div>,
            <div
                className={sharedClasses.threeDotsMenuItemContainer}
                onClick={() => handleEdit(row.id)}
                key="edit"
            >
                <EditIcon className={sharedClasses.threeDotsItemIcon} />
                <ListItemText primary={i18n.t('tableMaps.actionEdit')} />
            </div>,
            <div
                className={sharedClasses.threeDotsMenuItemContainer}
                onClick={() => setDeleteTarget(row)}
                key="delete"
            >
                <DeleteIcon className={sharedClasses.threeDotsItemIcon} />
                <ListItemText primary={i18n.t('tableMaps.actionDelete')} />
            </div>,
        ];
    }

    return (
        <Paper className={classes.paper}>
            <div className={sharedClasses.headerContainer}>
                <div className={classes.titleGroup}>
                    <Typography className={classes.title}>{i18n.t('tableMaps.title')}</Typography>
                    <Typography className={classes.subtitle}>
                        {i18n.t('tableMaps.subtitle')}
                    </Typography>
                </div>
                {isManagerOrOwner && (
                    <Button
                        variant="contained"
                        className={classes.createButton}
                        startIcon={<AddIcon />}
                        onClick={handleCreate}
                    >
                        {i18n.t('tableMaps.createButton')}
                    </Button>
                )}
            </div>

            <TableContainer style={{ flex: 1, marginTop: theme.spacing(2) }}>
                <Table stickyHeader className={sharedClasses.table} aria-label="table maps table">
                    <TableHead className={sharedClasses.tableHead}>
                        <TableRow>
                            <TableCell>{i18n.t('tableMaps.columnName')}</TableCell>
                            <TableCell>{i18n.t('tableMaps.columnTables')}</TableCell>
                            <TableCell>{i18n.t('tableMaps.columnTotalSeats')}</TableCell>
                            <TableCell>{i18n.t('tableMaps.columnStatus')}</TableCell>
                            <TableCell>{i18n.t('tableMaps.columnUpdated')}</TableCell>
                            <TableCell align="right" width={20} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {list.length > 0 ? (
                            list.map((row) => {
                                const updatedFormatted = formatUpdatedAt(row.updatedAt);
                                const isActive = row.status === 'Active';
                                return (
                                    <TableRow key={row.id}>
                                        <TableCell>
                                            <span
                                                className={classes.nameLink}
                                                onClick={() => handleView(row.id)}
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={(e) =>
                                                    e.key === 'Enter' && handleView(row.id)
                                                }
                                            >
                                                {row.name}
                                            </span>
                                        </TableCell>
                                        <TableCell>{row.tablesCount}</TableCell>
                                        <TableCell>{row.totalSeats}</TableCell>
                                        <TableCell>
                                            <div className={classes.statusCell}>
                                                <span
                                                    className={classes.statusDot}
                                                    style={{
                                                        backgroundColor: isActive
                                                            ? theme.palette.success.main
                                                            : theme.palette.secondary.main,
                                                    }}
                                                />
                                                {isActive
                                                    ? i18n.t('tableMaps.statusActive')
                                                    : i18n.t('tableMaps.statusInactive')}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {updatedFormatted ? (
                                                <>
                                                    <div className={classes.updatedDate}>
                                                        {updatedFormatted.date}
                                                    </div>
                                                    <div className={classes.updatedTime}>
                                                        {updatedFormatted.time}
                                                    </div>
                                                </>
                                            ) : (
                                                <div className={classes.updatedDate}>—</div>
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {isManagerOrOwner && (
                                                <BasicThreeDotsMenu items={buildMenuItems(row)} />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} style={{ height: 400 }}>
                                    <NothingFoundMessage />
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <PopupConfirmation
                open={deleteTarget !== null}
                close={() => setDeleteTarget(null)}
                title={i18n.t('tableMaps.deleteTitle')}
                description={i18n.t('tableMaps.deleteDescription')}
                activeBtn={i18n.t('tableMaps.actionDelete')}
                action={handleDeleteConfirm}
            />
        </Paper>
    );
}
